import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { apiZodios } from '~api/apiZodios'
import {
  type AgenticImplementationAttachment,
  type AgenticImplementationMessage,
  type IdentityId,
  type ProcessId,
} from '~types'

export type AgenticBuilderStatus = 'idle' | 'building' | 'waiting_for_user' | 'failed'

interface AgenticBuilderState {
  isOpen: boolean
  processId: ProcessId | null
  logId: string | null
  messages: AgenticImplementationMessage[]
  status: AgenticBuilderStatus
  statusMessage: string | null
  loading: boolean
  sending: boolean
  error: string | null
}

const initialState: AgenticBuilderState = {
  isOpen: false,
  processId: null,
  logId: null,
  messages: [],
  status: 'idle',
  statusMessage: null,
  loading: false,
  sending: false,
  error: null,
}

interface LogResponse {
  log_id: string
  messages: AgenticImplementationMessage[]
  status: string
  status_message?: string | null | undefined
}

export const fetchAgenticLog = createAsyncThunk(
  'agenticBuilder/fetchLog',
  async (args: {
    userId: string
    organizationIdentityId: IdentityId
    processId: ProcessId
  }): Promise<LogResponse> =>
    await apiZodios.agentic_implementation_getOrCreateAgenticImplementationLog({
      user_id: args.userId,
      organization_identity_id: args.organizationIdentityId,
      process_id: args.processId,
    }),
)

export const sendAgenticMessage = createAsyncThunk(
  'agenticBuilder/sendMessage',
  async (args: {
    userId: string
    organizationIdentityId: IdentityId
    processId: ProcessId
    content: string
    attachments?: AgenticImplementationAttachment[]
    displayContent?: string
  }): Promise<LogResponse> =>
    await apiZodios.agentic_implementation_appendAgenticImplementationMessage({
      user_id: args.userId,
      organization_identity_id: args.organizationIdentityId,
      process_id: args.processId,
      content: args.content,
      ...(args.displayContent ? { display_content: args.displayContent } : {}),
      ...(args.attachments?.length ? { attachments: args.attachments } : {}),
    }),
)

export const restartAgenticLog = createAsyncThunk(
  'agenticBuilder/restartLog',
  async (args: {
    userId: string
    organizationIdentityId: IdentityId
    processId: ProcessId
  }): Promise<LogResponse> =>
    await apiZodios.agentic_implementation_restartAgenticImplementationLog({
      user_id: args.userId,
      organization_identity_id: args.organizationIdentityId,
      process_id: args.processId,
    }),
)

const toStatus = (status: string): AgenticBuilderStatus =>
  status === 'building' || status === 'waiting_for_user' || status === 'failed' ? status : 'idle'

const applyLog = (state: AgenticBuilderState, log: LogResponse): void => {
  state.logId = log.log_id
  state.messages = log.messages
  state.status = toStatus(log.status)
  state.statusMessage = log.status_message ?? null
}

const agenticBuilderSlice = createSlice({
  name: 'agenticBuilder',
  initialState,
  reducers: {
    setAgenticBuilderOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload
    },
    resetAgenticBuilder: () => initialState,
    setAgenticBuilderProcess: (state, action: PayloadAction<ProcessId>) => {
      if (state.processId !== action.payload) {
        return { ...initialState, isOpen: state.isOpen, processId: action.payload }
      }
      return state
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgenticLog.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAgenticLog.fulfilled, (state, action) => {
        state.loading = false
        applyLog(state, action.payload)
      })
      .addCase(fetchAgenticLog.rejected, (state) => {
        state.loading = false
        state.error = 'Failed to load the AI builder conversation.'
      })
      .addCase(sendAgenticMessage.pending, (state) => {
        state.sending = true
        state.error = null
      })
      .addCase(sendAgenticMessage.fulfilled, (state, action) => {
        state.sending = false
        applyLog(state, action.payload)
      })
      .addCase(sendAgenticMessage.rejected, (state) => {
        state.sending = false
        state.error = 'Failed to send the message. Please try again.'
      })
      .addCase(restartAgenticLog.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(restartAgenticLog.fulfilled, (state, action) => {
        state.loading = false
        applyLog(state, action.payload)
      })
      .addCase(restartAgenticLog.rejected, (state) => {
        state.loading = false
        state.error = 'Failed to start a new chat. Please try again.'
      })
  },
})

export const { setAgenticBuilderOpen, resetAgenticBuilder, setAgenticBuilderProcess } =
  agenticBuilderSlice.actions

export default agenticBuilderSlice.reducer
